import gql from 'graphql-tag';
import React, {useCallback} from 'react';

import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible, assertAllAccessible} from 'graphql-client/InaccessibleInfo';
import {hasUnsavedChanges} from 'notebook/entries/BaseEntryUtil';
import TemplateActions from 'notebook/entry-templates/TemplateActions';
import TemplateDAO from 'notebook/entry-templates/TemplateDAO';
import {
  TemplatePublishingRecordsQuery,
  TemplatePublishingRecordsQueryVariables,
} from 'notebook/notes/tabs/__generated/TemplatePublishingRecordsQuery';
import PublishingNoteViewBanner from 'notebook/notes/tabs/PublishingNoteViewBanner';
import {TemplateNoteView, TemplateNoteViewProps} from 'notebook/notes/tabs/TemplateNoteView';
import {PublishingRecordForButton} from 'notebook/template-publishing/graphql/types';
import TemplatePublishingButton from 'notebook/template-publishing/TemplatePublishingButton';
import {getPublishableInfoForPublishingButton} from 'notebook/template-publishing/TemplatePublishingUtil';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

interface PublishingTemplateNoteViewProps extends TemplateNoteViewProps {
  openVersionApiId: string;
  openVersionModifiedAt: string;
}

export const TEMPLATE_PUBLISHING_RECORDS_QUERY = gql`
  query TemplatePublishingRecordsQuery($templateId: StrictString!) @skipAudit @engComponent(name: NOTEBOOK) {
    template(id: $templateId) {
      ... on TemplateNode {
        id
        ...PublishingRecordsForButtonGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${getFragmentWrapper('PublishingRecordsForButton').fragment}
`;

export default function PublishingTemplateNoteView(props: PublishingTemplateNoteViewProps): JSX.Element {
  const {fileId, openVersionApiId, openVersionModifiedAt, banner, isLatest} = props;

  const getPublishableInfoCallback = useCallback(
    (publishingRecords) =>
      getPublishableInfoForPublishingButton(
        publishingRecords,
        openVersionApiId,
        openVersionModifiedAt,
        isLatest
      ),
    [openVersionApiId, openVersionModifiedAt, isLatest]
  );

  const onPublishCallback = useCallback(
    (templateId, refetch) => async (versionName: string, versionDescription?: string) => {
      await TemplateDAO.publishVersion(templateId, openVersionApiId, versionName, versionDescription);
      refetch();
    },
    [openVersionApiId]
  );

  const onWithdrawCallback = useCallback(
    (templateId, refetch) => async () => {
      await TemplateDAO.withdrawVersion(templateId, openVersionApiId);
      refetch();
    },
    [openVersionApiId]
  );

  const onCreateDraftCallback = useCallback(
    (templateId) => async () => {
      const result = await TemplateDAO.createDraftFromVersion(templateId, openVersionApiId);
      if (result) {
        const templateVersionDbId = result.draftTemplateVersionDbId;
        TemplateActions.loadVersion(templateId, templateVersionDbId);
        TemplateActions.fetchVersions(templateId);
      }
    },
    [openVersionApiId]
  );

  return (
    <BenchlingQuery<TemplatePublishingRecordsQuery, TemplatePublishingRecordsQueryVariables>
      query={TEMPLATE_PUBLISHING_RECORDS_QUERY}
      variables={{templateId: fileId}}
      renderLoading={() => <CenteredHorizontalThrobber />}
    >
      {({template}, {refetch}) => {
        assert(!isInaccessible(template), 'Expected template to be accessible.');
        const publishingRecords: Array<PublishingRecordForButton> = assertAllAccessible(
          template.publishingRecords
        );

        let isViewingPublishedVersion = false;
        let bannerToSet = null;
        const publishingRecord = publishingRecords.find((pr) => pr.publishableVersionId === openVersionApiId);
        if (publishingRecord) {
          isViewingPublishedVersion = true;
          bannerToSet = (
            <PublishingNoteViewBanner
              onCreateDraft={onCreateDraftCallback(template.id)}
              publishingStatus={publishingRecord.publishingStatus}
            />
          );
        }

        return (
          <TemplateNoteView
            {...props}
            isViewingPublishedVersion={isViewingPublishedVersion}
            // Assumes that the banner set by the TemplateFile takes precedence over the banner set by this component.
            banner={banner || bannerToSet}
            titleBadge={
              <TemplatePublishingButton
                {...getPublishableInfoCallback(publishingRecords)}
                publishingRecords={publishingRecords}
                publishableModelType={ModelTypeConstants.TEMPLATE}
                onPublish={onPublishCallback(template.id, refetch)}
                onWithdraw={onWithdrawCallback(template.id, refetch)}
                onCreateDraft={onCreateDraftCallback(template.id)}
                isPublishOptionDisabled={hasUnsavedChanges(template.id)}
              />
            }
          />
        );
      }}
    </BenchlingQuery>
  );
}
